// The log's rows: one dense line each — when, what, who, where, about — and the whole line opens the
// row's detail. Everything else a row carries (its reason, its request, what changed, the full ids)
// is in the detail, so no id ever widens the table. Every row here is this organisation's own, so
// "where" is the venue alone; a row recorded against the organisation and no one venue says so.

import type { AuditEntry } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Avatar, Badge, Table, type TableColumn } from '@clavian/ui-web';
import { useMemo, type ReactNode } from 'react';

import { shortId } from './filters';
import type { AuditLabels, Directory } from './labels';
import type { AuditTimes } from './time';

/** An id too long for a cell: short, quiet and mono, with the whole of it on hover. */
export function ShortId({ id }: { readonly id: string }): ReactNode {
  return (
    <span className="console-audit-id" title={id}>
      {shortId(id)}
    </span>
  );
}

/** Where a row happened: the venue's name where it is known, its short id where not, the organisation otherwise. */
export function Place({ row, directory }: { readonly row: AuditEntry; readonly directory: Directory }): ReactNode {
  const { t } = useTranslation('console');
  if (row.venueId === null) return <span className="console-audit-place console-audit-quiet">{t('audit.acrossOrganization')}</span>;
  const venue = directory.venue(row.venueId);
  return (
    <span className="console-audit-place">
      {venue === undefined ? (
        <ShortId id={row.venueId} />
      ) : (
        <span className="console-audit-truncate" title={venue}>
          {venue}
        </span>
      )}
    </span>
  );
}

/** Who did it: their avatar and name, and what kind of actor they are; a break-glass grant is flagged. */
export function Actor({ row, full = false }: { readonly row: AuditEntry; readonly full?: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const name = row.actor.label ?? t(`audit.actorKind.${row.actor.kind}`);
  return (
    <span className="console-audit-actor">
      <Avatar name={name} size="xs" decorative />
      <span className="console-audit-truncate" title={name}>
        {name}
      </span>
      <Badge className="console-audit-kind">{t(full ? `audit.actorKind.${row.actor.kind}` : `audit.actorKindShort.${row.actor.kind}`)}</Badge>
      {/* Opened, the row says what the actor acted under on a line of its own. */}
      {row.onBehalfOf === undefined || full ? null : (
        <Badge tone="warn" className="console-audit-kind">
          {t(`audit.onBehalfOfShort.${row.onBehalfOf.kind}`)}
        </Badge>
      )}
    </span>
  );
}

export interface AuditTableProps {
  readonly rows: readonly AuditEntry[];
  readonly caption: string;
  readonly empty: ReactNode;
  readonly labels: AuditLabels;
  readonly directory: Directory;
  readonly times: AuditTimes;
  readonly onOpen: (row: AuditEntry) => void;
  /** When the rows arrived: "how long ago" is counted from it, not from each render. */
  readonly now: number;
}

export function AuditTable({ rows, caption, empty, labels, directory, times, onOpen, now }: AuditTableProps): ReactNode {
  const { t } = useTranslation('console');
  const columns = useMemo<readonly TableColumn<AuditEntry>[]>(
    () => [
      {
        key: 'when',
        header: t('audit.column.when'),
        cell: (row) => (
          <time className="console-audit-when" dateTime={row.occurredAt} title={`${times.local(row.occurredAt)}\n${times.utc(row.occurredAt)}`}>
            {times.relative(row.occurredAt, now)}
          </time>
        ),
      },
      {
        key: 'action',
        header: t('audit.column.action'),
        cell: (row) => (
          <span className="console-audit-action">
            {/* The row's one control. Its hit area is stretched over the whole row. */}
            <button type="button" className="console-audit-open" onClick={() => onOpen(row)}>
              {labels.action(row.action)}
            </button>
            <code className="console-audit-code">{row.action}</code>
          </span>
        ),
      },
      { key: 'actor', header: t('audit.column.actor'), cell: (row) => <Actor row={row} /> },
      { key: 'where', header: t('audit.column.where'), cell: (row) => <Place row={row} directory={directory} /> },
      {
        key: 'target',
        header: t('audit.column.target'),
        cell: (row) => (
          <span className="console-audit-about">
            <span className="console-audit-truncate">{labels.targetType(row.targetType)}</span>
            {row.targetId === null ? null : <ShortId id={row.targetId} />}
          </span>
        ),
      },
    ],
    [t, times, labels, directory, onOpen, now],
  );
  return <Table className="console-audit-table" caption={caption} captionHidden columns={columns} rows={rows} rowKey={(row) => row.id} empty={empty} />;
}
